import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "date-range-filter__label" }
const _hoisted_2 = {
  key: 0,
  class: "date-range-filter__value"
}
const _hoisted_3 = {
  key: 1,
  class: "date-range-filter__placeholder"
}
const _hoisted_4 = { class: "date-range-filter__body" }
const _hoisted_5 = { class: "date-range-filter__field-label" }
const _hoisted_6 = { class: "date-range-filter__field-text" }
const _hoisted_7 = { class: "date-range-filter__field-label" }
const _hoisted_8 = { class: "date-range-filter__field-text" }
const _hoisted_9 = { class: "date-range-filter__null-option" }
const _hoisted_10 = ["checked"]
const _hoisted_11 = { class: "date-range-filter__null-option-text" }
const _hoisted_12 = { class: "date-range-filter__actions" }

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { i18n } from '@/i18n';
import Calendar from '@/corePagesModule/components/Calendar.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'DateTimeRangeFilter',
  props: {
    label: {},
    modelValue: { default: () => [] },
    includeNull: { type: Boolean, default: false },
    hideTime: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "update:includeNull"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const t = i18n.t;

/**
 * クエリで受け取ったミリ秒文字列をDate型に変換する。
 * @param target - 変換対象の文字列
 */
const convertToDate = (target?: string): Date | null => {
  if (!target) return null;
  const parsed = Number(target);
  if (Number.isNaN(parsed)) return null;
  return new Date(parsed);
};

/**
 * 日時を表示用の文字列に整形する。
 * @param target - 整形対象のDate
 */
const formatDateTime = (target: Date | null): string => {
  if (!target) return '';
  const year = target.getFullYear();
  const month = `${target.getMonth() + 1}`.padStart(2, '0');
  const day = `${target.getDate()}`.padStart(2, '0');
  if (props.hideTime) return `${year}/${month}/${day}`;
  const hour = `${target.getHours()}`.padStart(2, '0');
  const minute = `${target.getMinutes()}`.padStart(2, '0');
  const second = `${target.getSeconds()}`.padStart(2, '0');
  return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
};

const isOpen = ref(false);
const activeField = ref<'from' | 'to'>('from');
const startDate = ref<Date | null>(convertToDate(props.modelValue?.[0]));
const endDate = ref<Date | null>(convertToDate(props.modelValue?.[1]));
const includeNull = ref(props.includeNull);
const calendarValue = ref<Date | null>(startDate.value ?? endDate.value ?? null);
const calendarRenderKey = ref(0);
const dropdownRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const startFieldRef = ref<HTMLElement | null>(null);
const endFieldRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const fieldsRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});
const calendarRef = ref<HTMLElement | null>(null);
const calendarStyle = ref<Record<string, string>>({});
const isCalendarVisible = ref(false);

/**
 * 選択中の開始・終了日時を親へ送出する。
 */
const emitModelValue = () => {
  if (!startDate.value && !endDate.value) {
    emit('update:modelValue', []);
    return;
  }
  const payload: string[] = ['', ''];
  if (startDate.value) payload[0] = startDate.value.getTime().toString();
  if (endDate.value) payload[1] = endDate.value.getTime().toString();
  emit('update:modelValue', payload);
};

watch(
  () => props.modelValue,
  (value) => {
    startDate.value = convertToDate(value?.[0]);
    endDate.value = convertToDate(value?.[1]);
    calendarValue.value = activeField.value === 'from' ? startDate.value : endDate.value;
  }
);

watch(
  () => props.includeNull,
  (value) => {
    includeNull.value = value;
  }
);

watch(activeField, (field) => {
  calendarValue.value = field === 'from' ? startDate.value : endDate.value;
  nextTick(updatePositions);
});

const displayText = computed(() => {
  const [start, end] = props.modelValue ?? [];
  const startFromModel = convertToDate(start);
  const endFromModel = convertToDate(end);
  if (startFromModel && endFromModel) {
    return `${formatDateTime(startFromModel)} - ${formatDateTime(endFromModel)}`;
  }
  if (startFromModel) return formatDateTime(startFromModel);
  if (endFromModel) return formatDateTime(endFromModel);
  return '';
});

/**
 * 開始日時選択ボタンを押した時の処理。
 */
const selectStart = () => {
  activeField.value = 'from';
  calendarValue.value = startDate.value;
  isOpen.value = true;
  isCalendarVisible.value = true;
  nextTick(updatePositions);
};

/**
 * 終了日時選択ボタンを押した時の処理。終了日時が未設定なら当月の空状態で開く。
 */
const selectEnd = () => {
  activeField.value = 'to';
  calendarValue.value = endDate.value;
  if (!endDate.value) {
    calendarRenderKey.value += 1;
  }
  isOpen.value = true;
  isCalendarVisible.value = true;
  nextTick(updatePositions);
};

/**
 * カレンダー選択値を適用する。
 * @param value - 選択された日時
 */
/**
 * カレンダーから受け取った日時を開始・終了に反映する。
 * @param value - 選択された日時
 */
const applyCalendarSelection = (value: Date | null) => {
  if (activeField.value === 'from') {
    if (props.hideTime && value) {
      value.setHours(0, 0, 0, 0);
    }
    startDate.value = value;
    if (value && endDate.value && value.getTime() > endDate.value.getTime()) {
      if (props.hideTime) {
        const cloned = new Date(value);
        cloned.setHours(23, 59, 59, 999);
        endDate.value = cloned;
      } else {
        endDate.value = value;
      }
    }
  } else {
    if (props.hideTime && value) {
      value.setHours(23, 59, 59, 999);
    }
    endDate.value = value;
    if (value && startDate.value && value.getTime() < startDate.value.getTime()) {
      if (props.hideTime) {
        const cloned = new Date(value);
        cloned.setHours(0, 0, 0, 0);
        startDate.value = cloned;
      } else {
        startDate.value = value;
      }
    }
  }
  emitModelValue();
};

/**
 * カレンダーのv-model更新を受け取る。
 * @param value - 選択された日時
 */
const onUpdateCalendar = (value: Date | null) => {
  applyCalendarSelection(value);
};

/**
 * 入力を全てクリアする。
 */
const clearRange = () => {
  startDate.value = null;
  endDate.value = null;
  calendarValue.value = null;
  includeNull.value = false;
  emitModelValue();
  emit('update:includeNull', false);
  isOpen.value = false;
  isCalendarVisible.value = false;
};

/**
 * 選択内容を親へ反映する。
 */
const applyRange = () => {
  emitModelValue();
  isOpen.value = false;
  isCalendarVisible.value = false;
};

/**
 * date-range-filter__fieldsとカレンダー以外をクリックしたら閉じる。
 * @param event - クリックイベント
 */
const handleClickOutside = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  if (!(target instanceof Node)) return;
  if (dropdownRef.value?.contains(target)) return;
  if (panelRef.value?.contains(target)) return;
  if (fieldsRef.value?.contains(target)) return;
  if (calendarRef.value?.contains(target)) return;
  if (triggerRef.value?.contains(target)) return;
  isOpen.value = false;
  isCalendarVisible.value = false;
};

/**
 * トリガーの下にパネルを配置する。
 */
const updatePanelPosition = () => {
  if (!isOpen.value) return;
  if (!triggerRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  panelStyle.value = {
    top: `${rect.bottom + window.scrollY + 8}px`,
    left: `${rect.left + window.scrollX}px`,
  };
};

/**
 * フィールドの右側にカレンダーを配置する。
 */
const updateCalendarPosition = () => {
  if (!isCalendarVisible.value) return;
  const anchor = activeField.value === 'from' ? startFieldRef.value : endFieldRef.value;
  if (!anchor) return;
  const rect = anchor.getBoundingClientRect();
  calendarStyle.value = {
    top: `${rect.top + window.scrollY}px`,
    left: `${rect.right + window.scrollX + 8}px`,
  };
};

/**
 * パネルとカレンダーの表示位置を再計算する。
 */
const updatePositions = () => {
  updatePanelPosition();
  updateCalendarPosition();
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', updatePositions);
  window.addEventListener('scroll', updatePositions, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', updatePositions);
  window.removeEventListener('scroll', updatePositions, true);
});

/**
 * トリガー入力の開閉をトグルする。
 */
const toggleDropdown = () => {
  if (isOpen.value) {
    isOpen.value = false;
    isCalendarVisible.value = false;
    return;
  }
  activeField.value = 'from';
  calendarValue.value = startDate.value ?? endDate.value ?? null;
  isOpen.value = true;
  isCalendarVisible.value = false;
  nextTick(updatePositions);
};

/**
 * 未設定のデータを含めるかを切り替える。
 * @param event - クリックイベント
 */
const onToggleIncludeNull = (event: Event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  includeNull.value = target.checked;
  emit('update:includeNull', target.checked);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "dropdownRef",
    ref: dropdownRef,
    class: "date-range-filter"
  }, [
    _createElementVNode("div", _hoisted_1, _toDisplayString(_ctx.label), 1),
    _createElementVNode("button", {
      ref_key: "triggerRef",
      ref: triggerRef,
      type: "button",
      class: "date-range-filter__trigger",
      onClick: toggleDropdown
    }, [
      (displayText.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(displayText.value), 1))
        : (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(t)('選択してください')), 1)),
      _createVNode(Mdi, {
        class: _normalizeClass(["date-range-filter__trigger-icon", { 'date-range-filter__trigger-icon--open': isOpen.value }]),
        icon: "expand_more"
      }, null, 8, ["class"])
    ], 512),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "panelRef",
            ref: panelRef,
            class: "date-range-filter__panel",
            style: _normalizeStyle(panelStyle.value)
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", {
                ref_key: "fieldsRef",
                ref: fieldsRef,
                class: "date-range-filter__fields"
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["date-range-filter__field", { 'date-range-filter__field--active': isOpen.value && activeField.value === 'from' }])
                }, [
                  _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)(_ctx.hideTime ? 'FilterStartDate' : 'FilterStartDateTime')), 1),
                  _createElementVNode("button", {
                    ref_key: "startFieldRef",
                    ref: startFieldRef,
                    type: "button",
                    class: "date-range-filter__field-input",
                    onClick: selectStart
                  }, [
                    _createElementVNode("span", _hoisted_6, _toDisplayString(startDate.value ? formatDateTime(startDate.value) : _unref(t)('選択してください')), 1),
                    _createVNode(Mdi, {
                      icon: "calendar_today",
                      class: "date-range-filter__field-icon"
                    })
                  ], 512)
                ], 2),
                _createElementVNode("div", {
                  class: _normalizeClass(["date-range-filter__field", { 'date-range-filter__field--active': isOpen.value && activeField.value === 'to' }])
                }, [
                  _createElementVNode("div", _hoisted_7, _toDisplayString(_unref(t)(_ctx.hideTime ? 'FilterEndDate' : 'FilterEndDateTime')), 1),
                  _createElementVNode("button", {
                    ref_key: "endFieldRef",
                    ref: endFieldRef,
                    type: "button",
                    class: "date-range-filter__field-input",
                    onClick: selectEnd
                  }, [
                    _createElementVNode("span", _hoisted_8, _toDisplayString(endDate.value ? formatDateTime(endDate.value) : _unref(t)('選択してください')), 1),
                    _createVNode(Mdi, {
                      icon: "calendar_today",
                      class: "date-range-filter__field-icon"
                    })
                  ], 512)
                ], 2)
              ], 512),
              _createElementVNode("label", _hoisted_9, [
                _createElementVNode("input", {
                  class: "date-range-filter__null-option-checkbox",
                  type: "checkbox",
                  checked: includeNull.value,
                  onChange: onToggleIncludeNull
                }, null, 40, _hoisted_10),
                _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)(_ctx.hideTime ? 'FilterIncludeNullDate' : 'FilterIncludeNullLastCheckedAt')), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createVNode(TextButton, {
                "button-style": 'btn-secondary',
                size: 'btn-sm',
                "on-click": clearRange
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('FilterClear')), 1)
                ], undefined, true),
                _: 1
              }),
              _createVNode(TextButton, {
                "button-style": 'btn-primary',
                size: 'btn-sm',
                "on-click": applyRange
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('FilterApply')), 1)
                ], undefined, true),
                _: 1
              })
            ])
          ], 4))
        : _createCommentVNode("", true)
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isCalendarVisible.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "calendarRef",
            ref: calendarRef,
            class: "date-range-filter__calendar-float",
            style: _normalizeStyle(calendarStyle.value)
          }, [
            (_openBlock(), _createBlock(Calendar, {
              key: calendarRenderKey.value,
              "model-value": calendarValue.value,
              "hide-time": _ctx.hideTime,
              "onUpdate:modelValue": onUpdateCalendar
            }, null, 8, ["model-value", "hide-time"]))
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ], 512))
}
}

})